// Broadcasts.jsx - FULLY RESPONSIVE for Android, iOS, Tablets, Desktop
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = '';

// ═══════════════════════════════════════════════════════════
// CUSTOM HOOK: useWindowSize – live viewport tracking
// ═══════════════════════════════════════════════════════════
function useWindowSize() {
  const [size, setSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800
  }));

  useEffect(() => {
    let rafId = null;
    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setSize({ w: window.innerWidth, h: window.innerHeight });
      });
    };
    window.addEventListener('resize', onResize);
    // Also listen for orientation change on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(onResize, 150); // delay for Safari to settle
    });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return size;
}

// ═══════════════════════════════════════════════════════════
// BREAKPOINT HELPERS
// ═══════════════════════════════════════════════════════════
// xs: 0-374    (iPhone SE, small Androids)
// sm: 375-479  (iPhone 12/13/14, standard Android)
// md: 480-767  (large phones, phablets landscape)
// lg: 768-1023 (iPad, Android tablets)
// xl: 1024+    (iPad Pro, laptops, desktops)

const Broadcasts = () => {
  const { w } = useWindowSize();
  const isXs = w < 375;
  const isSm = w >= 375 && w < 480;
  const isMd = w >= 480 && w < 768;
  const isLg = w >= 768 && w < 1024;
  const isXl = w >= 1024;
  const isMobile = w < 768;
  const isTablet = w >= 768 && w < 1024;
  const isDesktop = w >= 1024;

  // ═══════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════
  const [token] = useState(() => localStorage.getItem('token') || '');
  const [myInfo, setMyInfo] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sentBroadcasts, setSentBroadcasts] = useState([]);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastStats, setBroadcastStats] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [view, setView] = useState('received');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toggling, setToggling] = useState(null);

  const [formData, setFormData] = useState({
    target_role: 'All',
    subject: '',
    message_text: '',
    priority: 'Normal',
    expires_at: ''
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null); // { type: 'image'|'pdf', url, name }
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [filterUnread, setFilterUnread] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // ═══════════════════════════════════════════════════════════
  // MEMOIZED HEADERS
  // ═══════════════════════════════════════════════════════════
  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }),
    [token]
  );

  // ═══════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════
  const fetchMyInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/messaging/me`, { headers });
      if (res.ok) setMyInfo(await res.json());
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  }, [headers]);

  const fetchBroadcasts = useCallback(async () => {
    try {
      setLoading(true);
      const url = filterUnread
        ? `${API}/broadcasts?unread_only=true`
        : `${API}/broadcasts`;
      const res = await fetch(url, { headers });
      if (res.ok) setBroadcasts(await res.json());
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
      setError('Ntibyashobotse gukura amatangazo');
    } finally {
      setLoading(false);
    }
  }, [headers, filterUnread]);

  const fetchSentBroadcasts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/broadcasts/sent`, { headers });
      if (res.ok) setSentBroadcasts(await res.json());
    } catch (err) {
      console.error('Failed to fetch sent broadcasts:', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/broadcasts/unread-count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [headers]);

  const fetchBroadcastStats = useCallback(
    async (broadcastId) => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/broadcasts/${broadcastId}/stats`, { headers });
        if (res.ok) setBroadcastStats(await res.json());
      } catch (err) {
        console.error('Failed to fetch broadcast stats:', err);
      } finally {
        setLoading(false);
      }
    },
    [headers]
  );

  // ═══════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════
  const markAsRead = useCallback(
    async (broadcastId) => {
      try {
        await fetch(`${API}/broadcasts/${broadcastId}/read`, {
          method: 'PUT',
          headers
        });
        fetchBroadcasts();
        fetchUnreadCount();
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    },
    [headers, fetchBroadcasts, fetchUnreadCount]
  );

  const markAllAsRead = useCallback(async () => {
    try {
      const res = await fetch(`${API}/broadcasts/mark-all-read`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess(`✅ ${data.updated_count} amatangazo yasomwe`);
        fetchBroadcasts();
        fetchUnreadCount();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [headers, fetchBroadcasts, fetchUnreadCount]);

  const toggleBroadcast = useCallback(
    async (broadcastId, currentStatus) => {
      const action = currentStatus ? 'guhagarika' : 'gutangira';
      if (!window.confirm(`Urashaka ${action} iri tangazo?`)) return;

      setToggling(broadcastId);
      try {
        const res = await fetch(`${API}/broadcasts/${broadcastId}/toggle`, {
          method: 'PUT',
          headers
        });
        if (res.ok) {
          const data = await res.json();
          setSuccess(data.message);
          fetchSentBroadcasts();
        } else {
          const data = await res.json();
          setError(typeof data.error === 'string' ? data.error : 'Habaye ikosa');
        }
        setTimeout(() => { setSuccess(''); setError(''); }, 3000);
      } catch (err) {
        console.error('Failed to toggle broadcast:', err);
        setError('Habaye ikosa mu guhindura itangazo');
        setTimeout(() => setError(''), 3000);
      } finally {
        setToggling(null);
      }
    },
    [headers, fetchSentBroadcasts]
  );

  const resendBroadcast = useCallback(
    async (broadcastId) => {
      try {
        const res = await fetch(`${API}/broadcasts/${broadcastId}/resend`, {
          method: 'POST',
          headers
        });
        if (res.ok) {
          const data = await res.json();
          setSuccess(data.message);
          fetchSentBroadcasts();
          setTimeout(() => setSuccess(''), 4000);
        } else {
          const data = await res.json();
          setError(typeof data.error === 'string' ? data.error : 'Habaye ikosa');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        console.error('Failed to resend broadcast:', err);
        setError('Habaye ikosa');
        setTimeout(() => setError(''), 3000);
      }
    },
    [headers, fetchSentBroadcasts]
  );

  const deleteBroadcast = useCallback(
    async (broadcastId) => {
      if (!window.confirm('Urashaka gusiba iri tangazo burundu? Ntibizagaruka.')) return;
      try {
        const res = await fetch(`${API}/broadcasts/${broadcastId}`, {
          method: 'DELETE',
          headers
        });
        if (res.ok) {
          setSuccess('🗑️ Itangazo ryasibwe neza!');
          fetchSentBroadcasts();
          setView('sent');
          setTimeout(() => setSuccess(''), 3000);
        }
      } catch (err) {
        console.error('Failed to delete broadcast:', err);
        setError('Ntibyashobotse gusiba itangazo');
      }
    },
    [headers, fetchSentBroadcasts]
  );

  // ── File selection with validation + preview ──
  const handleFileSelect = useCallback((selectedFile) => {
    setFileError('');
    if (!selectedFile) { setFile(null); setFilePreview(null); return; }

    const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    const allowedExts = /\.(pdf|jpe?g|png|webp)$/i;
    const ext = selectedFile.name.split('.').pop();

    if (!allowedMimes.includes(selectedFile.type) || !allowedExts.test(selectedFile.name)) {
      setFileError('Dosiye igomba kuba PDF, JPG, PNG cyangwa WEBP gusa');
      setFile(null); setFilePreview(null);
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      setFileError('Dosiye ntigomba kurenza 5MB');
      setFile(null); setFilePreview(null);
      return;
    }

    setFile(selectedFile);
    if (selectedFile.type === 'application/pdf') {
      setFilePreview({ type: 'pdf', name: selectedFile.name });
    } else {
      const url = URL.createObjectURL(selectedFile);
      setFilePreview({ type: 'image', url, name: selectedFile.name });
    }
  }, []);

  const clearFile = useCallback(() => {
    if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
    setFile(null); setFilePreview(null); setFileError('');
    const input = document.getElementById('broadcast-file');
    if (input) input.value = '';
  }, [filePreview]);

  const createBroadcast = useCallback(
    async (e) => {
      e.preventDefault();
      if (!formData.message_text.trim()) {
        setError('Ubutumwa ni ngombwa');
        return;
      }
      if (fileError) { setError(fileError); return; }
      setSubmitting(true);
      setError('');

      try {
        const body = new FormData();
        body.append('target_role', formData.target_role);
        body.append('subject', formData.subject);
        body.append('message_text', formData.message_text);
        body.append('priority', formData.priority);
        if (formData.expires_at) body.append('expires_at', formData.expires_at);
        if (file) body.append('file', file);

        const res = await fetch(`${API}/broadcast`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body
        });
        const data = await res.json();

        if (res.ok) {
          setSuccess(`📢 Itangazo ryoherejwe neza! Abantu ${data.recipient_count} bazaribona.`);
          setFormData({ target_role: 'All', subject: '', message_text: '', priority: 'Normal', expires_at: '' });
          clearFile();
          fetchSentBroadcasts();
          setView('sent');
          setTimeout(() => setSuccess(''), 5000);
        } else {
          setError(typeof data.error === 'string' ? data.error : 'Habaye ikosa mu kohereza itangazo');
        }
      } catch (err) {
        console.error('Failed to create broadcast:', err);
        setError('Habaye ikosa mu kohereza itangazo');
      } finally {
        setSubmitting(false);
      }
    },
    [formData, file, fileError, token, fetchSentBroadcasts, clearFile]
  );

  const openBroadcastDetail = useCallback(
    async (broadcast) => {
      setSelectedBroadcast(broadcast);
      setView('detail');
      if (!broadcast.is_read) await markAsRead(broadcast.broadcast_id);
    },
    [markAsRead]
  );

  const openBroadcastStats = useCallback(
    async (broadcast) => {
      setSelectedBroadcast(broadcast);
      await fetchBroadcastStats(broadcast.broadcast_id);
      setView('stats');
    },
    [fetchBroadcastStats]
  );

  // ═══════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!token) return;
    fetchMyInfo();
    fetchBroadcasts();
    fetchUnreadCount();
  }, [token, fetchMyInfo, fetchBroadcasts, fetchUnreadCount]);

  useEffect(() => {
    if (!token) return;
    fetchBroadcasts();
  }, [token, filterUnread, fetchBroadcasts]);

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token, fetchUnreadCount]);

  // ═══════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Ubu';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString('rw-RW');
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('rw-RW', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredBroadcasts = useMemo(
    () =>
      broadcasts.filter(
        (b) =>
          b.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.message_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.sender_name?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [broadcasts, searchTerm]
  );

  const canSendBroadcasts = myInfo?.role === 'Akagari' || myInfo?.role === 'Supadmin';

  const getTargetLabel = (target) => {
    const labels = { All: '🌍 Bose', Mudugudu: '🏘️ Imidugudu', Akagari: '🏛️ Utuagari', Abandi: '👥 Abandi' };
    return labels[target] || target;
  };

  // ═══════════════════════════════════════════════════════════
  // RESPONSIVE STYLES – adapts to every screen
  // ═══════════════════════════════════════════════════════════
  const s = {
    // ── Global container ──
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isXs ? '8px' : isSm ? '10px' : isMd ? '14px' : isLg ? '18px' : '20px',
      paddingTop: isXs ? '8px' : isMobile ? '12px' : '20px',
      // iOS safe-area insets
      paddingBottom: 'env(safe-area-inset-bottom, 12px)',
      paddingLeft: `max(${isXs ? '8px' : isMobile ? '12px' : '20px'}, env(safe-area-inset-left, 0px))`,
      paddingRight: `max(${isXs ? '8px' : isMobile ? '12px' : '20px'}, env(safe-area-inset-right, 0px))`,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      minHeight: '100vh',
      background: '#f5f7fa',
      boxSizing: 'border-box',
      WebkitOverflowScrolling: 'touch' // smooth iOS scrolling
    },

    // ── Header ──
    header: {
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'space-between',
      alignItems: 'center',
      flexDirection: isMobile ? 'column' : 'row',
      marginBottom: isMobile ? '16px' : '24px',
      flexWrap: 'wrap',
      gap: isMobile ? '12px' : '16px',
      background: '#fff',
      padding: isXs ? '12px' : isMobile ? '14px 16px' : '20px',
      borderRadius: isMobile ? '10px' : '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    },

    title: {
      fontSize: isXs ? '18px' : isSm ? '20px' : '24px',
      fontWeight: '700',
      color: '#1a1a2e',
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '8px' : '12px',
      margin: 0,
      textAlign: isMobile ? 'center' : 'left'
    },

    badge: {
      background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
      color: '#fff',
      borderRadius: '20px',
      padding: isXs ? '3px 10px' : '4px 14px',
      fontSize: isXs ? '11px' : '13px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    },

    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '6px' : '10px',
      fontSize: isXs ? '12px' : '14px',
      color: '#666',
      background: '#f8f9fa',
      padding: isXs ? '6px 10px' : '8px 16px',
      borderRadius: '20px',
      maxWidth: '100%',
      overflow: 'hidden'
    },

    // ── Navigation Tabs ──
    navTabs: {
      display: 'flex',
      gap: isXs ? '4px' : '8px',
      marginBottom: isMobile ? '14px' : '20px',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      background: '#fff',
      padding: isXs ? '8px' : '12px',
      borderRadius: isMobile ? '10px' : '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      overflowX: isMobile ? 'auto' : 'visible',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none', // Firefox
      msOverflowStyle: 'none' // IE
    },

    navTab: (active) => ({
      padding: isXs ? '10px 14px' : isMobile ? '10px 16px' : '12px 24px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : isMobile ? '13px' : '14px',
      fontWeight: '600',
      background: active
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'transparent',
      color: active ? '#fff' : '#555',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '4px' : '8px',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      minHeight: '44px', // iOS minimum tap target
      WebkitTapHighlightColor: 'transparent'
    }),

    tabBadge: {
      background: '#e74c3c',
      color: '#fff',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      fontSize: '11px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold'
    },

    // ── Search & Filter Row ──
    searchFilterRow: {
      display: 'flex',
      gap: isXs ? '6px' : isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '14px' : '20px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },

    searchInput: {
      flex: 1,
      minWidth: isXs ? '100%' : isMobile ? '100%' : '250px',
      width: isMobile ? '100%' : 'auto',
      padding: isXs ? '10px 12px' : '12px 18px',
      border: '2px solid #e8e8e8',
      borderRadius: '10px',
      fontSize: isXs ? '14px' : '14px', // 14px minimum prevents iOS zoom
      outline: 'none',
      transition: 'border-color 0.3s',
      background: '#fff',
      boxSizing: 'border-box',
      WebkitAppearance: 'none', // iOS reset
      minHeight: '44px'
    },

    filterBtn: (active) => ({
      padding: isXs ? '10px 12px' : '12px 18px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : '13px',
      fontWeight: '600',
      background: active ? '#e74c3c' : '#fff',
      color: active ? '#fff' : '#555',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent',
      flex: isMobile ? '1' : 'none'
    }),

    actionBtn: {
      padding: isXs ? '10px 12px' : '12px 18px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : '13px',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(102,126,234,0.3)',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent',
      flex: isMobile ? '1' : 'none'
    },

    // ── Broadcast List ──
    broadcastList: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '8px' : '12px'
    },

    broadcastCard: (isRead) => ({
      background: '#fff',
      borderRadius: isMobile ? '10px' : '12px',
      padding: isXs ? '12px' : isMobile ? '14px' : '20px',
      boxShadow: isRead
        ? '0 2px 8px rgba(0,0,0,0.04)'
        : '0 4px 15px rgba(102,126,234,0.12)',
      cursor: 'pointer',
      transition: 'all 0.3s',
      borderLeft: isRead ? '4px solid #e0e0e0' : '4px solid #667eea',
      position: 'relative',
      WebkitTapHighlightColor: 'rgba(102,126,234,0.1)'
    }),

    sentCard: (isActive) => ({
      background: '#fff',
      borderRadius: isMobile ? '10px' : '12px',
      padding: isXs ? '12px' : isMobile ? '14px' : '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      transition: 'all 0.3s',
      borderLeft: isActive ? '4px solid #27ae60' : '4px solid #e74c3c',
      position: 'relative',
      opacity: isActive ? 1 : 0.75
    }),

    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'flex-start',
      marginBottom: isXs ? '8px' : '12px',
      gap: '8px',
      flexDirection: isXs ? 'column' : 'row'
    },

    cardTitle: {
      fontSize: isXs ? '14px' : isMobile ? '15px' : '16px',
      fontWeight: '600',
      color: '#1a1a2e',
      marginBottom: '6px',
      wordBreak: 'break-word'
    },

    cardMeta: {
      fontSize: isXs ? '10px' : '12px',
      color: '#888',
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '4px' : '8px',
      flexWrap: 'wrap'
    },

    priorityBadge: (priority) => ({
      padding: isXs ? '2px 8px' : '4px 12px',
      borderRadius: '15px',
      fontSize: isXs ? '10px' : '11px',
      fontWeight: '700',
      background:
        priority === 'Urgent'
          ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
          : priority === 'High'
          ? 'linear-gradient(135deg, #f39c12, #e67e22)'
          : priority === 'Low'
          ? '#95a5a6'
          : 'linear-gradient(135deg, #3498db, #2980b9)',
      color: '#fff',
      whiteSpace: 'nowrap'
    }),

    targetBadge: {
      padding: isXs ? '2px 8px' : '4px 12px',
      borderRadius: '15px',
      fontSize: isXs ? '10px' : '11px',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
      color: '#fff',
      whiteSpace: 'nowrap'
    },

    statusBadge: (isActive) => ({
      padding: isXs ? '2px 8px' : '4px 12px',
      borderRadius: '15px',
      fontSize: isXs ? '10px' : '11px',
      fontWeight: '700',
      background: isActive
        ? 'linear-gradient(135deg, #27ae60, #2ecc71)'
        : 'linear-gradient(135deg, #e74c3c, #c0392b)',
      color: '#fff',
      whiteSpace: 'nowrap'
    }),

    cardBody: {
      fontSize: isXs ? '13px' : '14px',
      color: '#555',
      lineHeight: '1.7',
      marginBottom: isXs ? '8px' : '12px',
      wordBreak: 'break-word'
    },

    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      paddingTop: isXs ? '8px' : '12px',
      borderTop: '1px solid #f0f2f5',
      flexWrap: 'wrap',
      gap: '10px'
    },

    senderInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '8px' : '10px'
    },

    senderAvatar: {
      width: isXs ? '30px' : '36px',
      height: isXs ? '30px' : '36px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: isXs ? '12px' : '14px',
      fontWeight: '700',
      flexShrink: 0
    },

    attachmentBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: isXs ? '4px 8px' : '6px 12px',
      background: '#f8f9fa',
      borderRadius: '15px',
      fontSize: isXs ? '11px' : '12px',
      color: '#555'
    },

    unreadDot: {
      position: 'absolute',
      top: isXs ? '12px' : '20px',
      right: isXs ? '12px' : '20px',
      width: isXs ? '10px' : '12px',
      height: isXs ? '10px' : '12px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      boxShadow: '0 2px 6px rgba(102,126,234,0.4)'
    },

    // ── Empty State ──
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? '40px 16px' : '80px 20px',
      color: '#888',
      background: '#fff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },

    emptyIcon: {
      fontSize: isMobile ? '48px' : '64px',
      marginBottom: isMobile ? '12px' : '20px'
    },

    // ── Form ──
    formContainer: {
      background: '#fff',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isXs ? '16px' : isMobile ? '20px' : '32px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    },

    formTitle: {
      fontSize: isXs ? '18px' : isMobile ? '19px' : '22px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: isMobile ? '20px' : '28px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },

    formGroup: {
      marginBottom: isMobile ? '16px' : '22px'
    },

    formLabel: {
      display: 'block',
      fontSize: isXs ? '13px' : '14px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '8px'
    },

    formInput: {
      width: '100%',
      padding: isXs ? '12px 14px' : '14px 18px',
      border: '2px solid #e8e8e8',
      borderRadius: '10px',
      fontSize: '16px', // 16px prevents iOS zoom on focus
      outline: 'none',
      transition: 'border-color 0.3s',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
      minHeight: '48px'
    },

    formTextarea: {
      width: '100%',
      padding: isXs ? '12px 14px' : '14px 18px',
      border: '2px solid #e8e8e8',
      borderRadius: '10px',
      fontSize: '16px',
      outline: 'none',
      minHeight: isMobile ? '120px' : '160px',
      resize: 'vertical',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      WebkitAppearance: 'none'
    },

    formSelect: {
      width: '100%',
      padding: isXs ? '12px 14px' : '14px 18px',
      border: '2px solid #e8e8e8',
      borderRadius: '10px',
      fontSize: '16px',
      outline: 'none',
      background: '#fff',
      cursor: 'pointer',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
      minHeight: '48px',
      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 14px center',
      paddingRight: '40px'
    },

    formRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: isMobile ? '0' : '18px'
    },

    fileInputWrapper: {
      marginTop: '8px'
    },

    fileInput: {
      display: 'none'
    },

    fileLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: isXs ? '12px 16px' : '14px 24px',
      background: '#f8f9fa',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: isXs ? '13px' : '14px',
      color: '#555',
      transition: 'all 0.2s',
      border: '2px dashed #ddd',
      minHeight: '44px',
      width: isMobile ? '100%' : 'auto',
      justifyContent: isMobile ? 'center' : 'flex-start',
      boxSizing: 'border-box'
    },

    selectedFile: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      background: '#e8f5e9',
      borderRadius: '8px',
      marginTop: '12px',
      fontSize: '13px',
      color: '#2e7d32',
      wordBreak: 'break-all'
    },

    submitBtn: {
      width: '100%',
      padding: isMobile ? '14px' : '16px',
      border: 'none',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '12px',
      boxShadow: '0 4px 15px rgba(102,126,234,0.3)',
      minHeight: '50px',
      WebkitTapHighlightColor: 'transparent'
    },

    // ── Detail View ──
    detailContainer: {
      background: '#fff',
      borderRadius: isMobile ? '12px' : '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    },

    detailHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isXs ? '16px' : isMobile ? '20px' : '28px',
      color: '#fff'
    },

    detailTitle: {
      fontSize: isXs ? '18px' : isMobile ? '20px' : '24px',
      fontWeight: '700',
      marginBottom: '12px',
      wordBreak: 'break-word'
    },

    detailMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '8px' : isMobile ? '10px' : '18px',
      fontSize: isXs ? '12px' : '14px',
      opacity: 0.95,
      flexWrap: 'wrap'
    },

    detailBody: {
      padding: isXs ? '16px' : isMobile ? '20px' : '28px'
    },

    detailMessage: {
      fontSize: isXs ? '14px' : isMobile ? '15px' : '16px',
      lineHeight: '1.9',
      color: '#333',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    },

    backBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: isXs ? '10px 14px' : '12px 20px',
      background: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#555',
      marginBottom: isMobile ? '14px' : '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      fontWeight: '500',
      minHeight: '44px',
      WebkitTapHighlightColor: 'transparent'
    },

    // ── Stats ──
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isXs
        ? 'repeat(2, 1fr)'
        : isMobile
        ? 'repeat(2, 1fr)'
        : 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: isXs ? '8px' : isMobile ? '10px' : '16px',
      marginBottom: isMobile ? '16px' : '24px'
    },

    statCard: {
      background: '#fff',
      borderRadius: isMobile ? '10px' : '12px',
      padding: isXs ? '14px 10px' : isMobile ? '16px' : '24px',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    },

    statNumber: {
      fontSize: isXs ? '24px' : isMobile ? '28px' : '36px',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent'
    },

    statLabel: {
      fontSize: isXs ? '11px' : '13px',
      color: '#888',
      marginTop: '6px',
      fontWeight: '500'
    },

    recipientList: {
      background: '#fff',
      borderRadius: isMobile ? '10px' : '12px',
      padding: isXs ? '14px' : isMobile ? '16px' : '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      marginBottom: isMobile ? '14px' : '20px'
    },

    recipientItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isXs ? '10px 0' : '14px 0',
      borderBottom: '1px solid #f0f2f5',
      flexDirection: isXs ? 'column' : 'row',
      gap: isXs ? '6px' : '0'
    },

    // ── Alerts ──
    alert: (type) => ({
      padding: isXs ? '12px 14px' : '16px 20px',
      borderRadius: '10px',
      marginBottom: isMobile ? '12px' : '20px',
      fontSize: isXs ? '13px' : '14px',
      fontWeight: '500',
      background: type === 'error' ? '#fef2f2' : '#ecfdf5',
      color: type === 'error' ? '#dc2626' : '#059669',
      border: `1px solid ${type === 'error' ? '#fecaca' : '#a7f3d0'}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      wordBreak: 'break-word'
    }),

    // ── Stats Row (sent cards) ──
    statsRow: {
      display: 'flex',
      gap: isXs ? '6px' : '12px',
      marginTop: isXs ? '8px' : '14px',
      flexWrap: 'wrap'
    },

    miniStat: {
      display: 'flex',
      alignItems: 'center',
      gap: isXs ? '4px' : '6px',
      fontSize: isXs ? '11px' : '13px',
      color: '#666',
      background: '#f8f9fa',
      padding: isXs ? '4px 8px' : '6px 12px',
      borderRadius: '6px',
      whiteSpace: 'nowrap'
    },

    // ── Toggle / Resend / Delete buttons ──
    toggleBtn: (isActive) => ({
      padding: isXs ? '8px 12px' : '10px 18px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : '13px',
      fontWeight: '600',
      background: isActive
        ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
        : 'linear-gradient(135deg, #27ae60, #2ecc71)',
      color: '#fff',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      boxShadow: isActive
        ? '0 2px 8px rgba(231,76,60,0.3)'
        : '0 2px 8px rgba(39,174,96,0.3)',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent'
    }),

    resendBtn: {
      padding: isXs ? '8px 12px' : '10px 18px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : '13px',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #3498db, #2980b9)',
      color: '#fff',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent'
    },

    deleteBtn: {
      padding: isXs ? '8px 12px' : '10px 18px',
      border: 'none',
      borderRadius: '6px',
      background: '#fef2f2',
      color: '#dc2626',
      cursor: 'pointer',
      fontSize: isXs ? '12px' : '13px',
      fontWeight: '600',
      transition: 'all 0.2s',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      WebkitTapHighlightColor: 'transparent'
    },

    downloadBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: isXs ? '12px 16px' : '14px 24px',
      background: '#f8f9fa',
      borderRadius: '10px',
      color: '#333',
      textDecoration: 'none',
      fontWeight: '600',
      marginTop: isMobile ? '16px' : '24px',
      transition: 'all 0.2s',
      wordBreak: 'break-all',
      minHeight: '44px'
    },

    expiryWarning: {
      marginTop: isMobile ? '16px' : '24px',
      padding: isXs ? '10px 12px' : '14px 18px',
      background: '#fffbeb',
      borderRadius: '10px',
      color: '#b45309',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: isXs ? '13px' : '14px',
      flexWrap: 'wrap'
    },

    buttonGroup: {
      display: 'flex',
      gap: isXs ? '6px' : '8px',
      flexWrap: 'wrap',
      alignItems: 'center',
      width: isMobile ? '100%' : 'auto'
    },

    inactiveOverlay: {
      position: 'absolute',
      top: isXs ? '8px' : '12px',
      right: isXs ? '8px' : '12px',
      background: 'rgba(231,76,60,0.9)',
      color: '#fff',
      padding: isXs ? '3px 8px' : '4px 10px',
      borderRadius: '6px',
      fontSize: isXs ? '10px' : '11px',
      fontWeight: '700',
      zIndex: 1
    },

    // ── Timestamp ──
    timestamp: {
      fontSize: isXs ? '11px' : '13px',
      color: '#888',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      alignSelf: isXs ? 'flex-end' : 'auto'
    },

    // ── Role badge in header ──
    roleBadge: {
      background: '#667eea',
      color: '#fff',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: isXs ? '10px' : '11px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    },

    // ── Sender name/role block ──
    senderName: {
      fontWeight: '600',
      fontSize: isXs ? '12px' : '13px',
      color: '#333'
    },

    senderRole: {
      fontSize: isXs ? '10px' : '11px',
      color: '#888'
    },

    // ── Dismiss button for alerts ──
    dismissBtn: {
      marginLeft: 'auto',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      color: 'inherit',
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }
  };

  // ═══════════════════════════════════════════════════════════
  // GLOBAL STYLE TAG – scrollbar hiding, safe-area, iOS fixes
  // ═══════════════════════════════════════════════════════════
  const globalCSS = `
    /* Hide scrollbar on nav tabs (all browsers) */
    *::-webkit-scrollbar { width: 0; height: 0; }
    
    /* iOS safe area */
    @supports (padding: env(safe-area-inset-top)) {
      .broadcasts-root {
        padding-top: env(safe-area-inset-top);
      }
    }
    
    /* Prevent iOS text size adjust */
    html { -webkit-text-size-adjust: 100%; }
    
    /* Smooth scrolling */
    html { scroll-behavior: smooth; }
    
    /* Focus visible for accessibility */
    *:focus-visible {
      outline: 3px solid #667eea;
      outline-offset: 2px;
    }
    
    /* Remove tap highlight on mobile */
    * { -webkit-tap-highlight-color: transparent; }
    
    /* iOS input zoom prevention – inputs must be ≥16px */
    @media screen and (max-width: 767px) {
      input, select, textarea { font-size: 16px !important; }
    }
    
    /* Print styles */
    @media print {
      .broadcasts-root { background: #fff !important; }
    }
  `;

  // ═══════════════════════════════════════════════════════════
  // NO TOKEN VIEW
  // ═══════════════════════════════════════════════════════════
  if (!token) {
    return (
      <>
        <style>{globalCSS}</style>
        <div
          className="broadcasts-root"
          style={{
            ...s.emptyState,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f7fa',
            padding: isMobile ? '20px' : '40px'
          }}
        >
          <div style={s.emptyIcon}>🔒</div>
          <h2 style={{ marginBottom: '10px', color: '#333', fontSize: isMobile ? '18px' : '22px', textAlign: 'center' }}>
            Banza winjire (Login)
          </h2>
          <p style={{ color: '#666', textAlign: 'center', fontSize: isMobile ? '14px' : '16px' }}>
            Ukeneye token kugira ngo ukoreshe amatangazo
          </p>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      <style>{globalCSS}</style>
      <div className="broadcasts-root" style={s.container}>

        {/* ════════════ HEADER ════════════ */}
        <div style={s.header}>
          <h1 style={s.title}>
            📢 {isMobile ? 'Amatangazo' : 'Amatangazo'}
            {unreadCount > 0 && (
              <span style={s.badge}>{unreadCount} {isXs ? '' : 'bishya'}</span>
            )}
          </h1>
          {myInfo && (
            <div style={s.userInfo}>
              <span style={s.senderAvatar}>
                {myInfo.fullname?.charAt(0)?.toUpperCase() || '?'}
              </span>
              {!isXs && <span>{myInfo.fullname}</span>}
              <span style={s.roleBadge}>{myInfo.role}</span>
            </div>
          )}
        </div>

        {/* ════════════ ALERTS ════════════ */}
        {error && (
          <div style={s.alert('error')}>
            <span>❌</span>
            <span style={{ flex: 1 }}>{error}</span>
            <button onClick={() => setError('')} style={s.dismissBtn}>×</button>
          </div>
        )}
        {success && (
          <div style={s.alert('success')}>
            <span>✅</span>
            <span style={{ flex: 1 }}>{success}</span>
            <button onClick={() => setSuccess('')} style={s.dismissBtn}>×</button>
          </div>
        )}

        {/* ════════════ NAVIGATION TABS ════════════ */}
        <div style={s.navTabs}>
          <button
            style={s.navTab(view === 'received' || view === 'detail')}
            onClick={() => { setView('received'); fetchBroadcasts(); }}
          >
            📥 {isXs ? '' : 'Nahawe'}
            {unreadCount > 0 && <span style={s.tabBadge}>{unreadCount}</span>}
          </button>

          {canSendBroadcasts && (
            <>
              <button
                style={s.navTab(view === 'sent' || view === 'stats')}
                onClick={() => { setView('sent'); fetchSentBroadcasts(); }}
              >
                📤 {isXs ? '' : 'Nohereje'}
              </button>
              <button
                style={s.navTab(view === 'create')}
                onClick={() => setView('create')}
              >
                ✏️ {isXs ? 'Rishya' : 'Itangazo Rishya'}
              </button>
            </>
          )}
        </div>

        {/* ════════════ RECEIVED BROADCASTS VIEW ════════════ */}
        {view === 'received' && (
          <>
            <div style={s.searchFilterRow}>
              <input
                style={s.searchInput}
                placeholder="🔍 Shakisha..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                style={s.filterBtn(filterUnread)}
                onClick={() => setFilterUnread((prev) => !prev)}
              >
                {filterUnread ? '✓' : '○'} {isXs ? '' : 'Atarasomwe'}
              </button>
              {unreadCount > 0 && (
                <button style={s.actionBtn} onClick={markAllAsRead}>
                  ✓ {isXs ? '' : 'Soma byose'}
                </button>
              )}
            </div>

            {loading ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '16px' }}>⏳</div>
                <p>Gutegereza...</p>
              </div>
            ) : filteredBroadcasts.length === 0 ? (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>📭</div>
                <h3 style={{ marginBottom: '10px', color: '#333', fontSize: isMobile ? '16px' : '18px' }}>
                  Nta matangazo
                </h3>
                <p style={{ fontSize: isMobile ? '13px' : '14px' }}>Nta matangazo ufite ubu</p>
              </div>
            ) : (
              <div style={s.broadcastList}>
                {filteredBroadcasts.map((broadcast) => (
                  <div
                    key={broadcast.broadcast_id}
                    style={s.broadcastCard(broadcast.is_read)}
                    onClick={() => openBroadcastDetail(broadcast)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openBroadcastDetail(broadcast);
                      }
                    }}
                  >
                    {!broadcast.is_read && <div style={s.unreadDot} />}

                    <div style={s.cardHeader}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.cardTitle}>
                          {broadcast.subject || 'Itangazo'}
                        </div>
                        <div style={s.cardMeta}>
                          <span style={s.priorityBadge(broadcast.priority)}>
                            {broadcast.priority}
                          </span>
                          <span style={s.targetBadge}>
                            → {getTargetLabel(broadcast.target_role)}
                          </span>
                        </div>
                      </div>
                      <div style={s.timestamp}>
                        {formatTime(broadcast.created_at)}
                      </div>
                    </div>

                    <div style={s.cardBody}>
                      {broadcast.message_text?.substring(0, isMobile ? 120 : 180)}
                      {(broadcast.message_text?.length || 0) > (isMobile ? 120 : 180) ? '...' : ''}
                    </div>

                    <div style={s.cardFooter}>
                      <div style={s.senderInfo}>
                        <div style={s.senderAvatar}>
                          {broadcast.sender_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div style={s.senderName}>{broadcast.sender_name}</div>
                          <div style={s.senderRole}>{broadcast.sender_role}</div>
                        </div>
                      </div>
                      {broadcast.file_path && (
                        <div style={s.attachmentBadge}>📎 {isXs ? '' : 'Inyandiko'}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ════════════ SENT BROADCASTS VIEW ════════════ */}
        {view === 'sent' && (
          <>
            {loading ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '16px' }}>⏳</div>
                <p>Gutegereza...</p>
              </div>
            ) : sentBroadcasts.length === 0 ? (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>📤</div>
                <h3 style={{ marginBottom: '10px', color: '#333', fontSize: isMobile ? '16px' : '18px' }}>
                  Nta matangazo wohereje
                </h3>
                <p style={{ fontSize: isMobile ? '13px' : '14px' }}>
                  Kanda "✏️ Itangazo Rishya" kugira utangire
                </p>
              </div>
            ) : (
              <div style={s.broadcastList}>
                {sentBroadcasts.map((broadcast) => (
                  <div
                    key={broadcast.broadcast_id}
                    style={s.sentCard(broadcast.is_active)}
                  >
                    {!broadcast.is_active && (
                      <div style={s.inactiveOverlay}>⏸️ {isXs ? 'OFF' : 'RYAHAGARITSWE'}</div>
                    )}

                    <div style={s.cardHeader}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.cardTitle}>
                          {broadcast.subject || 'Itangazo'}
                        </div>
                        <div style={s.cardMeta}>
                          <span style={s.priorityBadge(broadcast.priority)}>
                            {broadcast.priority}
                          </span>
                          {!isXs && (
                            <span style={s.targetBadge}>
                              → {getTargetLabel(broadcast.target_role)}
                            </span>
                          )}
                          <span style={s.statusBadge(broadcast.is_active)}>
                            {broadcast.is_active ? '🟢' : '🔴'} {isXs ? '' : broadcast.is_active ? 'Active' : 'Off'}
                          </span>
                        </div>
                      </div>
                      <div style={s.timestamp}>
                        {formatTime(broadcast.created_at)}
                      </div>
                    </div>

                    <div style={s.cardBody}>
                      {broadcast.message_text?.substring(0, isMobile ? 100 : 150)}
                      {(broadcast.message_text?.length || 0) > (isMobile ? 100 : 150) ? '...' : ''}
                    </div>

                    <div style={s.statsRow}>
                      <div style={s.miniStat}>
                        👥 {broadcast.total_recipients || 0}
                      </div>
                      <div style={s.miniStat}>
                        ✅ {broadcast.read_count || 0}
                      </div>
                      <div style={{ ...s.miniStat, background: '#ecfdf5', color: '#059669' }}>
                        📊{' '}
                        {broadcast.total_recipients > 0
                          ? Math.round(((broadcast.read_count || 0) / broadcast.total_recipients) * 100)
                          : 0}%
                      </div>
                    </div>

                    <div style={{ ...s.cardFooter, marginTop: '16px' }}>
                      <div style={s.buttonGroup}>
                        <button style={s.actionBtn} onClick={() => openBroadcastStats(broadcast)}>
                          📊 {isXs ? '' : 'Ibisobanuro'}
                        </button>

                        <button
                          style={s.toggleBtn(broadcast.is_active)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBroadcast(broadcast.broadcast_id, broadcast.is_active);
                          }}
                          disabled={toggling === broadcast.broadcast_id}
                        >
                          {toggling === broadcast.broadcast_id
                            ? '⏳'
                            : broadcast.is_active
                            ? `⏸️ ${isXs ? '' : 'Hagarika'}`
                            : `▶️ ${isXs ? '' : 'Tangira'}`}
                        </button>

                        <button
                          style={s.resendBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            resendBroadcast(broadcast.broadcast_id);
                          }}
                          title="Ohereza ku bantu bashya"
                        >
                          🔄 {isXs ? '' : 'Ohereza'}
                        </button>
                      </div>

                      <button
                        style={s.deleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBroadcast(broadcast.broadcast_id);
                        }}
                      >
                        🗑️ {isXs ? '' : 'Siba'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ════════════ CREATE BROADCAST VIEW ════════════ */}
        {view === 'create' && (
          <div style={s.formContainer}>
            <h2 style={s.formTitle}>📢 {isMobile ? 'Itangazo Rishya' : 'Ohereza Itangazo Rishya'}</h2>

            <form onSubmit={createBroadcast}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>📍 Kohereza</label>
                  <select
                    style={s.formSelect}
                    value={formData.target_role}
                    onChange={(e) => setFormData((p) => ({ ...p, target_role: e.target.value }))}
                  >
                    <option value="All">🌍 Bose</option>
                    <option value="Mudugudu">🏘️ Imidugudu gusa</option>
                    <option value="Akagari">🏛️ Utuagari gusa</option>
                    <option value="Abandi">👥 Abandi gusa</option>
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>⚡ Priority</label>
                  <select
                    style={s.formSelect}
                    value={formData.priority}
                    onChange={(e) => setFormData((p) => ({ ...p, priority: e.target.value }))}
                  >
                    <option value="Low">🟢 Low</option>
                    <option value="Normal">🔵 Normal</option>
                    <option value="High">🟡 High</option>
                    <option value="Urgent">🔴 Urgent</option>
                  </select>
                </div>
              </div>

              <div style={s.formGroup}>
                <label style={s.formLabel}>📝 Umutwe</label>
                <input
                  style={s.formInput}
                  type="text"
                  placeholder="Umutwe w'itangazo..."
                  value={formData.subject}
                  onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.formLabel}>💬 Ubutumwa *</label>
                <textarea
                  style={s.formTextarea}
                  placeholder="Andika ubutumwa hano..."
                  value={formData.message_text}
                  onChange={(e) => setFormData((p) => ({ ...p, message_text: e.target.value }))}
                  required
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.formLabel}>⏰ Izarangirira (Optional)</label>
                <input
                  style={s.formInput}
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData((p) => ({ ...p, expires_at: e.target.value }))}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.formLabel}>📎 Inyandiko/Ifoto (Optional)</label>
                <div style={s.fileInputWrapper}>
                  <input
                    type="file"
                    id="broadcast-file"
                    style={s.fileInput}
                    accept=".pdf,image/*"
                    onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  />
                  <label htmlFor="broadcast-file" style={s.fileLabel}>
                    📁 Hitamo inyandiko...
                  </label>
                </div>

                {/* File error */}
                {fileError && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: '8px', fontSize: '13px' }}>
                    ❌ {fileError}
                  </div>
                )}

                {/* File preview */}
                {filePreview && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: filePreview.type === 'image' ? '10px' : '0' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#0369a1' }}>
                        {filePreview.type === 'pdf' ? '📄' : '🖼️'} {filePreview.name}
                      </span>
                      <button type="button" onClick={clearFile} style={{ ...s.dismissBtn, color: '#dc2626', minWidth: '32px', minHeight: '32px' }}>×</button>
                    </div>
                    {filePreview.type === 'image' && (
                      <img
                        src={filePreview.url}
                        alt="preview"
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'contain', display: 'block' }}
                      />
                    )}
                    {filePreview.type === 'pdf' && (
                      <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '4px' }}>
                        PDF — {(file.size / 1024).toFixed(1)} KB • Iri tangazo rizagaragara nk'inyandiko
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                style={{
                  ...s.submitBtn,
                  opacity: submitting || !formData.message_text.trim() ? 0.6 : 1,
                  cursor: submitting ? 'wait' : 'pointer'
                }}
                disabled={submitting || !formData.message_text.trim()}
              >
                {submitting ? '⏳ Kohereza...' : '📢 Ohereza Itangazo'}
              </button>
            </form>
          </div>
        )}

        {/* ════════════ BROADCAST DETAIL VIEW ════════════ */}
        {view === 'detail' && selectedBroadcast && (
          <>
            <button style={s.backBtn} onClick={() => setView('received')}>
              ← {isXs ? 'Inyuma' : 'Subira inyuma'}
            </button>

            <div style={s.detailContainer}>
              <div style={s.detailHeader}>
                <div style={s.detailTitle}>
                  {selectedBroadcast.subject || 'Itangazo'}
                </div>
                <div style={s.detailMeta}>
                  <span style={s.priorityBadge(selectedBroadcast.priority)}>
                    {selectedBroadcast.priority}
                  </span>
                  <span>👤 {selectedBroadcast.sender_name}</span>
                  {!isXs && <span>🕐 {formatFullDate(selectedBroadcast.created_at)}</span>}
                </div>
                {isXs && (
                  <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.9 }}>
                    🕐 {formatFullDate(selectedBroadcast.created_at)}
                  </div>
                )}
              </div>

              <div style={s.detailBody}>
                <div style={s.detailMessage}>
                  {selectedBroadcast.message_text}
                </div>

                {selectedBroadcast.file_path && (() => {
                  const isImage = /\.(jpe?g|png|webp)$/i.test(selectedBroadcast.file_path);
                  const isPdf = /\.pdf$/i.test(selectedBroadcast.file_path);
                  const fileUrl = `${API}${selectedBroadcast.file_path}`;
                  return (
                    <div style={{ marginTop: isMobile ? '16px' : '24px' }}>
                      {isImage && (
                        <div style={{ marginBottom: '12px' }}>
                          <img
                            src={fileUrl}
                            alt="attachment"
                            style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '10px', objectFit: 'contain', display: 'block', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}
                          />
                        </div>
                      )}
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={s.downloadBtn}
                      >
                        {isPdf ? '📄 Reba PDF' : '🖼️ Reba Ifoto'} — {selectedBroadcast.file_name || 'Download'}
                      </a>
                    </div>
                  );
                })()}

                {selectedBroadcast.expires_at && (
                  <div style={s.expiryWarning}>
                    <span>⏰</span>
                    <span>
                      {isXs ? 'Izarangira:' : 'Iri tangazo rizarangira:'}{' '}
                      {formatFullDate(selectedBroadcast.expires_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ════════════ BROADCAST STATS VIEW ════════════ */}
        {view === 'stats' && selectedBroadcast && broadcastStats && (
          <>
            <button style={s.backBtn} onClick={() => setView('sent')}>
              ← {isXs ? 'Inyuma' : 'Subira inyuma'}
            </button>

            <h2 style={{
              marginBottom: isMobile ? '16px' : '24px',
              color: '#1a1a2e',
              fontSize: isMobile ? '18px' : '22px',
              wordBreak: 'break-word'
            }}>
              📊 {isXs ? '' : 'Ibisobanuro: '}{selectedBroadcast.subject || 'Itangazo'}
            </h2>

            <div style={s.statsGrid}>
              <div style={s.statCard}>
                <div style={s.statNumber}>
                  {broadcastStats.stats?.total_recipients || 0}
                </div>
                <div style={s.statLabel}>👥 Bahawe</div>
              </div>
              <div style={s.statCard}>
                <div style={{
                  ...s.statNumber,
                  background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {broadcastStats.stats?.read_count || 0}
                </div>
                <div style={s.statLabel}>✅ Basomye</div>
              </div>
              <div style={s.statCard}>
                <div style={{
                  ...s.statNumber,
                  background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {broadcastStats.stats?.unread_count || 0}
                </div>
                <div style={s.statLabel}>⏳ Batarasoma</div>
              </div>
              <div style={s.statCard}>
                <div style={{
                  ...s.statNumber,
                  background: 'linear-gradient(135deg, #f39c12, #e67e22)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {broadcastStats.stats?.total_recipients > 0
                    ? Math.round(((broadcastStats.stats.read_count || 0) / broadcastStats.stats.total_recipients) * 100)
                    : 0}%
                </div>
                <div style={s.statLabel}>📈 Ijanisha</div>
              </div>
            </div>

            {/* Read recipients */}
            <div style={s.recipientList}>
              <h3 style={{ marginBottom: isMobile ? '14px' : '20px', color: '#333', fontSize: isMobile ? '16px' : '18px' }}>
                ✅ Abasomye ({broadcastStats.read_recipients?.length || 0})
              </h3>
              {!broadcastStats.read_recipients?.length ? (
                <p style={{ color: '#888', textAlign: 'center', padding: isMobile ? '20px' : '30px', fontSize: isMobile ? '13px' : '14px' }}>
                  Nta muntu warasoma iri tangazo
                </p>
              ) : (
                broadcastStats.read_recipients.map((recipient, index) => (
                  <div key={`read-${index}`} style={s.recipientItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isXs ? '8px' : '12px' }}>
                      <div style={s.senderAvatar}>
                        {recipient.fullname?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={s.senderName}>{recipient.fullname}</div>
                        <div style={s.senderRole}>{recipient.user_role}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: isXs ? '11px' : '12px', color: '#888' }}>
                      🕐 {formatFullDate(recipient.read_at)}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Unread recipients */}
            {broadcastStats.unread_recipients?.length > 0 && (
              <div style={s.recipientList}>
                <h3 style={{ marginBottom: isMobile ? '14px' : '20px', color: '#e74c3c', fontSize: isMobile ? '16px' : '18px' }}>
                  ⏳ Batarasoma ({broadcastStats.unread_recipients.length})
                </h3>
                {broadcastStats.unread_recipients.map((recipient, index) => (
                  <div key={`unread-${index}`} style={s.recipientItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isXs ? '8px' : '12px' }}>
                      <div style={{ ...s.senderAvatar, background: '#ccc' }}>
                        {recipient.fullname?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={s.senderName}>{recipient.fullname}</div>
                        <div style={s.senderRole}>{recipient.user_role}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: isXs ? '11px' : '12px', color: '#e74c3c', fontWeight: '600' }}>
                      ⏳ {isXs ? '' : 'Ntiyarasoma'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Broadcasts;